import React, { useEffect, useId, useMemo, useRef, useState, useLayoutEffect } from 'react';
import './sidePopup.scss';

type SidePopupWidth = number | string;
export type SidePopupProps = {
    /** Управляет открытием */
    open: boolean;
    /** Закрыть попап */
    onClose: () => void;
    /** Заголовок */
    title?: string;
    /** Контент */
    children: React.ReactNode;
    /** Ширина на десктопе, например 420, "420px", "35vw". По умолчанию "35vw" */
    width?: SidePopupWidth;
    /** Минимальная ширина на десктопе. По умолчанию 360 */
    minWidth?: number;
    /** Максимальная ширина на десктопе. По умолчанию 720 */
    maxWidth?: number;
    /** z-index (если нужно поверх всего). По умолчанию 15 */
    zIndex?: number;
    /** Закрывать по клику на затемнение. По умолчанию true */
    closeOnOverlayClick?: boolean;
    /** Закрывать по Esc. По умолчанию true */
    closeOnEsc?: boolean;
    /** Блокировать скролл фона. По умолчанию true */
    lockBodyScroll?: boolean;
    /** Доп. класс для модификаций */
    className?: string;
    /** aria-label для кнопки закрытия */
    closeAriaLabel?: string;
    /** Показывать крестик. По умолчанию true */
    showCloseButton?: boolean;
};

const ANIMATION_MS = 280;

const toCssSize = (v: SidePopupWidth | undefined, fallback: string): string => {
    if (v == null) return fallback;
    if (typeof v === 'number') return `${v}px`;
    const s = String(v).trim();
    return s.length ? s : fallback;
};

const getFocusable = (root: HTMLElement): HTMLElement[] => {
    const selectors = [
        'a[href]',
        'button:not([disabled])',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
        '[contenteditable="true"]',
    ].join(',');
    return Array.from(root.querySelectorAll<HTMLElement>(selectors)).filter((el) => {
        const style = window.getComputedStyle(el);
        return style.visibility !== 'hidden' && style.display !== 'none';
    });
};

const SidePopup: React.FC<SidePopupProps> = ({
    open,
    onClose,
    title,
    children,
    width = '60vw',
    minWidth = 360,
    maxWidth = 555,
    zIndex = 15,
    closeOnOverlayClick = true,
    closeOnEsc = true,
    lockBodyScroll = true,
    className,
    closeAriaLabel = 'Закрыть',
    showCloseButton = true,
}) => {
    const titleId = useId();
    const panelRef = useRef<HTMLDivElement | null>(null);
    const lastActiveElRef = useRef<HTMLElement | null>(null);
    const [mounted, setMounted] = useState(open);
    const [visible, setVisible] = useState(open);
    const desktopWidth = useMemo(() => toCssSize(width, '35vw'), [width]);

    useEffect(() => {
        if (open) {
            lastActiveElRef.current = (document.activeElement as HTMLElement) ?? null;
            setMounted(true);
            const t = window.setTimeout(() => setVisible(true), 10);
            return () => window.clearTimeout(t);
        }
        setVisible(false);
        const t = window.setTimeout(() => {
            setMounted(false);
            lastActiveElRef.current?.focus?.();
        }, ANIMATION_MS);
        return () => window.clearTimeout(t);
    }, [open]);

    useLayoutEffect(() => {
        if (!lockBodyScroll || !open) return;
        const html = document.documentElement;
        const body = document.body;
        const prevHtmlOverflow = html.style.overflow;
        const prevBodyOverflow = body.style.overflow;
        const prevBodyPaddingRight = body.style.paddingRight;
        const scrollBarWidth = window.innerWidth - html.clientWidth;
        html.style.overflow = 'hidden';
        body.style.overflow = 'hidden';
        if (scrollBarWidth) body.style.paddingRight = `${scrollBarWidth}px`;
        return () => {
            html.style.overflow = prevHtmlOverflow;
            body.style.overflow = prevBodyOverflow;
            body.style.paddingRight = prevBodyPaddingRight;
        };
    }, [open, lockBodyScroll]);

    useEffect(() => {
        if (!mounted || !closeOnEsc) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                onClose();
                return;
            }
            if (e.key === 'Tab') {
                const panel = panelRef.current;
                if (!panel) return;
                const focusables = getFocusable(panel);
                if (!focusables.length) return;
                const first = focusables[0];
                const last = focusables[focusables.length - 1];
                const active = document.activeElement as HTMLElement | null;
                if (e.shiftKey) {
                    if (!active || active === first || !panel.contains(active)) {
                        e.preventDefault();
                        last.focus();
                    }
                } else {
                    if (!active || active === last || !panel.contains(active)) {
                        e.preventDefault();
                        first.focus();
                    }
                }
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [mounted, closeOnEsc, onClose]);

    useEffect(() => {
        if (!mounted) return;
        const t = window.setTimeout(() => {
            const panel = panelRef.current;
            if (!panel) return;
            const focusables = getFocusable(panel);
            if (focusables.length) focusables[0].focus();
            else panel.focus();
        }, 20);
        return () => window.clearTimeout(t);
    }, [mounted]);

    if (!mounted) return null;

    const rootClass = ['sidePopup', visible ? 'sidePopup--open' : 'sidePopup--closed', className].filter(Boolean).join(' ');

    const panelStyle: React.CSSProperties = {
        width: desktopWidth,
        minWidth: `${minWidth}px`,
        maxWidth: `${maxWidth}px`,
    };

    const onOverlayMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
        if (!closeOnOverlayClick) return;
        if (e.target === e.currentTarget) onClose();
    };

    return (
        <div className={rootClass} style={{ zIndex }}>
            <div className="sidePopup__overlay" onMouseDown={onOverlayMouseDown} />

            <div
                ref={panelRef}
                className="sidePopup__panel"
                role="dialog"
                aria-modal="true"
                aria-labelledby={title ? titleId : undefined}
                tabIndex={-1}
                style={panelStyle}
            >
                <div className="sidePopup__header">
                    {title ? (
                        <h2 className="sidePopup__title" id={titleId}>
                            {title}
                        </h2>
                    ) : (
                        <div className="sidePopup__title-spacer" />
                    )}

                    {showCloseButton && (
                        <button className="sidePopup__close" type="button" onClick={onClose} aria-label={closeAriaLabel}>
                            <img className="sidePopup__close-icon" src="/icons/new/close.svg" alt="Закрыть" />
                        </button>
                    )}
                </div>

                <div className="sidePopup__body">{children}</div>
            </div>
        </div>
    );
};

export default SidePopup;
